import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../../constants/dataAids';
import { VIDEO_TYPE } from '../../../../constants/constants';
import PlayVideoIcon from '../../icons/PlayVideoIcon';
import * as Metrics from '../../utils/trackMetric';
import * as MetricEvents from '../../../../constants/metrics';
import { Carousel } from '../../lazy';
import CarouselArrow from './CarouselArrow';

import { useCarouselChanger } from './useCarouselChanger';

const SELECTED_BORDER_SPACING = 4;
const THUMB_SIZE = 50 + SELECTED_BORDER_SPACING;

const PADDING = 3;
const THUMB_SPACE = THUMB_SIZE + PADDING;
const HALF_THUMB_SIZE = THUMB_SPACE / 2;

const trackStyles = ({ assetsCount }) => ({
  marginLeft: `-${HALF_THUMB_SIZE * assetsCount}px`
});

const verticalCarousel = {
  container: {
    transform: 'rotateZ(90deg)',
    backfaceVisibility: 'hidden'
  },
  containerInner: {
    marginTop: -HALF_THUMB_SIZE * 3,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  slide: {
    opacity: 1,
    transform: 'rotateZ(-90deg)'
  }
};

const horizontalCarousel = {
  containerInner: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  slide: {
    opacity: 1
  }
};

export const styles = {
  thumbWrapper: ({ isCurrentAsset }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    padding: `${SELECTED_BORDER_SPACING}px`,
    borderColor: isCurrentAsset ? 'lowContrast' : 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid'
  }),
  thumbImage: {
    width: '100%',
    height: '100%',
    zIndex: '2',
    cursor: 'pointer',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  videoOverlay: {
    backgroundColor: 'rgb(0, 0, 0, 0.3)',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    margin: `${SELECTED_BORDER_SPACING}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  },
  playVideo: {
    width: '30px',
    height: '20px',
    zIndex: 1
  }
};

function CarouselThumbs(props) {
  const {
    Element: { Block },
    Component: { Background }
  } = UX2;
  const {
    hasFinalizedAssets,
    isCurrentAsset,
    onClickThumbnail,
    assets,
    assetsVisible,
    vertical = false,
    currentAssetIndex
  } = props;

  const carouselRef = useCarouselChanger(currentAssetIndex, assetsVisible, assets.length);

  if (!hasFinalizedAssets) {
    return null;
  }

  const carouselStyles = vertical ? verticalCarousel : horizontalCarousel;
  const hasMoreThanVisible = assets.length > assetsVisible;
  const assetsToDisplay = hasMoreThanVisible ? assetsVisible : assets.length;

  const getBackgroundImage = assetData => {
    let metadata = assetData?.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (error) {
        metadata = null;
      }
    }

    return (
      metadata?.thumbnail ||
      assetData?.small_url ||
      assetData?.url ||
      assetData?.attachment_file_name
    );
  };

  const onClick = index => {
    Metrics.track(MetricEvents.IMAGE_THUMBNAIL_CLICK);
    onClickThumbnail(index);
  };

  const arrowControls = [
    {
      component: CarouselArrow,
      position: 'top',
      props: { icon: 'chevronLeft', currentAssetIndex, onClick }
    },
    {
      component: CarouselArrow,
      position: 'bottom',
      props: { icon: 'chevronRight', currentAssetIndex, onClick }
    }
  ];

  return (
    <Carousel
      ref={ carouselRef }
      style={{
        ...carouselStyles,
        track: trackStyles({
          assetsCount: hasMoreThanVisible ? assetsVisible - 1 : assets.length - 1
        })
      }}
      width='fit-content'
      clickToNavigate={ false }
      dots={ false }
      arrows={ false }
      draggable={ false }
      infinite={ false }
      lazyLoad={ false }
      slideWidth={ THUMB_SIZE }
      slideHeight={ THUMB_SIZE }
      viewportWidth={ THUMB_SPACE * assetsToDisplay }
      cellPadding={ PADDING }
      controls={ hasMoreThanVisible ? arrowControls : [] }
    >
      { assets.map((assetData, index) => (
        <Block
          key={ index }
          onClick={ () => onClick(index) }
          style={{ ...styles.thumbWrapper({ isCurrentAsset: isCurrentAsset(index) }) }}
        >
          <Background
            data-aid={ dataAids.PRODUCT_ASSET_THUMBNAIL_RENDERED }
            backgroundImage={ getBackgroundImage(assetData) }
            style={ styles.thumbImage }
          />
          { (assetData?.type === VIDEO_TYPE || typeof assetData?.metadata === 'string') && (
            <Block
              style={ styles.videoOverlay }
              children={ <PlayVideoIcon style={ styles.playVideo } /> }
            />
          ) }
        </Block>
      )) }
    </Carousel>
  );
}

CarouselThumbs.propTypes = {
  assetsVisible: PropTypes.number,
  assets: PropTypes.array,
  hasFinalizedAssets: PropTypes.bool,
  isCurrentAsset: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  vertical: PropTypes.bool,
  currentAssetIndex: PropTypes.number
};

export default CarouselThumbs;
